// components/session/SessionManagement/ModalStyles.js
import styled, { keyframes } from 'styled-components';

const fadeIn = keyframes`
    from { opacity: 0; }
    to { opacity: 1; }
`;

const slideUp = keyframes`
    from { transform: translateY(20px); opacity: 0; }
    to { transform: translateY(0); opacity: 1; }
`;

const spin = keyframes`
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
`;

export const ModalOverlay = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    padding: ${props => props.theme.spacing.xl};
    z-index: ${props => props.theme.zIndex.modal};
    animation: ${fadeIn} 0.2s ease-out;
`;

export const ModalContent = styled.div`
    background: white;
    border-radius: ${props => props.theme.borderRadius.lg};
    width: 100%;
    max-width: 600px;
    max-height: 90vh;
    overflow-y: auto;
    animation: ${slideUp} 0.3s ease-out;

    @media (max-width: ${props => props.theme.breakpoints.sm}) {
        max-height: 100vh;
        margin: 0;
        border-radius: 0;
    }
`;

export const ModalHeader = styled.div`
    padding: ${props => props.theme.spacing.xl};
    border-bottom: 1px solid ${props => props.theme.colors.border};
    display: flex;
    align-items: center;
    justify-content: space-between;
    position: sticky;
    top: 0;
    background: white;
    z-index: 1;

    h2 {
        font-size: ${props => props.theme.typography.sizes['2xl']};
        color: ${props => props.theme.colors.text.primary};
        margin: 0;
    }

    .close-button {
        background: none;
        border: none;
        color: ${props => props.theme.colors.text.secondary};
        cursor: pointer;
        padding: ${props => props.theme.spacing.xs};
        border-radius: ${props => props.theme.borderRadius.full};
        display: flex;
        align-items: center;
        justify-content: center;
        transition: all ${props => props.theme.transitions.fast};

        &:hover {
            background: ${props => props.theme.colors.background.alt};
            color: ${props => props.theme.colors.text.primary};
        }
    }
`;

export const ModalBody = styled.div`
    padding: ${props => props.theme.spacing.xl};

    .section {
        margin-bottom: ${props => props.theme.spacing.xl};

        h3 {
            font-size: ${props => props.theme.typography.sizes.xl};
            margin-bottom: ${props => props.theme.spacing.sm};
            color: ${props => props.theme.colors.text.primary};
        }

        .hint {
            color: ${props => props.theme.colors.text.secondary};
            margin-bottom: ${props => props.theme.spacing.lg};
        }

        &.summary {
            padding: ${props => props.theme.spacing.lg};
            background: ${props => props.theme.colors.background.alt};
            border-radius: ${props => props.theme.borderRadius.md};
        }
    }

    .current-session,
    .new-session {
        font-size: ${props => props.theme.typography.sizes.lg};
        color: ${props => props.theme.colors.text.primary};
        font-weight: ${props => props.theme.typography.weights.medium};
    }

    .no-slots {
        text-align: center;
        color: ${props => props.theme.colors.text.secondary};
        padding: ${props => props.theme.spacing.xl};
    }

    .date-picker-container {
        .react-datepicker {
            width: 100%;
            font-family: inherit;
        }
    }
`;

export const TimeSlotGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
    gap: ${props => props.theme.spacing.md};
    margin-top: ${props => props.theme.spacing.lg};
`;

export const TimeSlot = styled.button`
    padding: ${props => props.theme.spacing.md};
    border: 1px solid ${props => props.selected ? props.theme.colors.primary.main : props.theme.colors.border};
    border-radius: ${props => props.theme.borderRadius.md};
    background: ${props => props.selected ? props.theme.colors.primary.main : 'white'};
    color: ${props => props.selected ? 'white' : props.theme.colors.text.primary};
    transition: all ${props => props.theme.transitions.normal};
    cursor: pointer;

    &:hover {
        border-color: ${props => props.theme.colors.primary.main};
        background: ${props => props.selected ? props.theme.colors.primary.main : props.theme.colors.primary.main + '15'};
    }
`;

export const ModalFooter = styled.div`
    padding: ${props => props.theme.spacing.xl};
    border-top: 1px solid ${props => props.theme.colors.border};
    display: flex;
    justify-content: flex-end;
    gap: ${props => props.theme.spacing.md};
    background: ${props => props.theme.colors.background.alt};
    position: sticky;
    bottom: 0;

    @media (max-width: ${props => props.theme.breakpoints.sm}) {
        padding: ${props => props.theme.spacing.lg};
    }
`;

export const LoadingSpinner = styled.div`
    position: relative;
    width: 40px;
    height: 40px;
    margin: ${props => props.theme.spacing.xl} auto;

    &:before {
        content: '';
        box-sizing: border-box;
        position: absolute;
        top: 50%;
        left: 50%;
        width: 40px;
        height: 40px;
        margin-top: -20px;
        margin-left: -20px;
        border-radius: 50%;
        border: 3px solid ${props => props.theme.colors.text.disabled};
        border-top-color: ${props => props.theme.colors.primary.main};
        animation: ${spin} 0.8s linear infinite;
    }
`;